import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import StockImageSummaryPanel from "./StockImageSummaryPanel";
import { IPart } from "../../../models/Part";

export type StockLightboxImage = {
  /** Preview/thumbnail URL (or data URL). Shown immediately. */
  url: string;
  /** Optional document/image id used by `resolveFullUrl` to lazy-load a higher-res version. */
  id?: string;
  alt?: string;
  caption?: string;
};

interface StockImageModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  images: StockLightboxImage[];
  initialIndex?: number;
  title?: string;
  /**
   * Optional resolver that fetches the full-resolution URL for an image on demand.
   * When provided and the displayed image has an `id`, the modal will fetch the
   * higher-res URL and swap it in while keeping the thumbnail visible until it loads.
   * The resolver result is cached per image id.
   */
  resolveFullUrl?: (image: StockLightboxImage) => Promise<string | null | undefined>;
  /**
   * Optional part to render a summary panel alongside the images —
   * mirrors the Stock detail header (title, vehicle, VRM, grade,
   * price, time-in-stock) and exposes a "View full stock page"
   * button. Leave unset for the plain lightbox behaviour used by
   * vehicle/donor image galleries.
   */
  part?: IPart;
}

export default function StockImageModal({
  open,
  setOpen,
  images,
  initialIndex = 0,
  title,
  resolveFullUrl,
  part,
}: StockImageModalProps) {
  const [index, setIndex] = useState(initialIndex);
  const fullUrlCache = useRef<Record<string, string>>({});
  const [, forceRerender] = useState(0);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open || !resolveFullUrl) return;
    const img = images[index];
    if (!img?.id || fullUrlCache.current[img.id]) return;
    let cancelled = false;
    resolveFullUrl(img).then((fullUrl) => {
      if (cancelled || !fullUrl || !img.id) return;
      fullUrlCache.current[img.id] = fullUrl;
      forceRerender((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [open, index, images, resolveFullUrl]);

  const total = images.length;

  const next = useCallback(() => {
    if (total === 0) return;
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total === 0) return;
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  const current = images[index];
  const currentDisplayUrl =
    current?.id && fullUrlCache.current[current.id]
      ? fullUrlCache.current[current.id]
      : current?.url;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className={
                "relative w-full " +
                (part ? "max-w-6xl" : "max-w-5xl")
              }
            >
              <div className="flex items-center justify-between text-white mb-2 px-1">
                <div className="min-w-0">
                  {title && <p className="text-sm font-medium truncate">{title}</p>}
                  {total > 0 && (
                    <p className="text-xs text-white/70">
                      {index + 1} of {total}
                      {current?.caption ? ` · ${current.caption}` : ""}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="rounded-full p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div
                className={
                  "grid gap-4 " +
                  (part ? "md:grid-cols-[minmax(0,1fr)_20rem]" : "")
                }
              >
                <div className="min-w-0">
                  <div className="relative flex items-center justify-center rounded-lg bg-black/40 ring-1 ring-white/10 overflow-hidden">
                    {total > 1 && (
                      <button
                        type="button"
                        onClick={prev}
                        aria-label="Previous image"
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/40 p-2 text-white/80 hover:bg-black/60 hover:text-white"
                      >
                        <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    )}

                    <div className="flex items-center justify-center w-full h-[70vh]">
                      {current ? (
                        <img
                          key={currentDisplayUrl}
                          src={currentDisplayUrl}
                          alt={current.alt ?? ""}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-white/60">
                          <PhotoIcon className="h-16 w-16" aria-hidden="true" />
                          <span className="mt-2 text-sm">No images</span>
                        </div>
                      )}
                    </div>

                    {total > 1 && (
                      <button
                        type="button"
                        onClick={next}
                        aria-label="Next image"
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/40 p-2 text-white/80 hover:bg-black/60 hover:text-white"
                      >
                        <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  {total > 1 && (
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                      {images.map((img, i) => (
                        <button
                          key={`${img.url}-${i}`}
                          type="button"
                          onClick={() => setIndex(i)}
                          aria-label={`Show image ${i + 1}`}
                          aria-current={i === index}
                          className={
                            "h-14 w-14 overflow-hidden rounded border-2 transition-all " +
                            (i === index
                              ? "border-white"
                              : "border-transparent opacity-60 hover:opacity-100")
                          }
                        >
                          <img
                            src={img.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {part && (
                  <aside className="min-w-0">
                    <StockImageSummaryPanel part={part} onDark />
                  </aside>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
